import { useCallback, useEffect, useRef, useState } from 'react';
import service from '../../services/ClientPortalService';
import { isAuthenticationRecovery } from '../../services/sessionCoordinator';

const initialLoad = { status: 'loading', message: '' };
const idleAction = { status: 'idle', key: '', message: '' };
export const participationKey = lesson => JSON.stringify([lesson.lessonId, lesson.studentId]);
export const portalScope = user => JSON.stringify(user ? [user.id, user.schoolId, user.role, user.clientId, user.activeContextKey, user.sessionId, user.sessionVersion] : []);

export default function useClientPortal(authScope, canAccess, signOut, billingYear) {
  const scope = JSON.stringify([authScope, billingYear]);
  const [data, setData] = useState({ scope, snapshot: null, loadState: initialLoad, actionState: idleAction });
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const mounted = useRef(false);
  const reads = useRef(0);
  const lifecycle = useRef(0);
  const busy = useRef(false);
  const stateRef = useRef(data);
  stateRef.current = data;
  const valid = useCallback(() => mounted.current && currentScope.current === scope, [scope]);
  const update = useCallback((change) => {
    if (!valid()) return;
    setData(previous => ({ ...(previous.scope === scope ? previous : { scope, snapshot: null, loadState: initialLoad, actionState: idleAction }), ...change }));
  }, [scope, valid]);
  const accessFailure = useCallback((error) => {
    if (isAuthenticationRecovery(error)) return true;
    const unauthorized = error.code === 401 || error.status === 401 || error.message === 'Unauthorized';
    if (!unauthorized && error.code !== 403 && error.status !== 403) return false;
    update({ snapshot: null, loadState: { status: 'denied', message: unauthorized ? 'Sesja wygasła. Zaloguj się ponownie.' : 'Brak uprawnień' }, actionState: idleAction });
    if (unauthorized && valid()) signOut();
    return true;
  }, [update, signOut, valid]);
  const refresh = useCallback(async () => {
    if (!canAccess || !valid()) return;
    const generation = ++reads.current;
    update({ loadState: { status: stateRef.current.scope === scope && stateRef.current.snapshot ? 'refreshing' : 'loading', message: '' } });
    try {
      const snapshot = await (billingYear == null ? service.getClientPortal() : service.getClientPortal(billingYear));
      if (!valid() || reads.current !== generation) return;
      update({ snapshot, loadState: { status: 'ready', message: '' } });
    } catch (error) {
      if (!valid() || reads.current !== generation || accessFailure(error)) return;
      update({ loadState: { status: stateRef.current.scope === scope && stateRef.current.snapshot ? 'stale' : 'error', message: 'Nie udało się pobrać danych portalu. Spróbuj ponownie.' } });
    }
  }, [scope, billingYear, canAccess, valid, update, accessFailure]);
  useEffect(() => {
    mounted.current = true;
    lifecycle.current += 1;
    busy.current = false;
    update({ snapshot: null, loadState: initialLoad, actionState: idleAction });
    refresh();
    return () => { mounted.current = false; reads.current += 1; lifecycle.current += 1; };
  }, [scope, canAccess, refresh, update]);

  const command = async (method, lesson) => {
    if (!valid() || busy.current || stateRef.current.scope !== scope || stateRef.current.loadState.status !== 'ready') return false;
    const generation = lifecycle.current;
    const currentCommand = () => valid() && lifecycle.current === generation;
    busy.current = true;
    update({ actionState: { status: 'saving', key: participationKey(lesson), message: '' } });
    try {
      await method({ lessonId: lesson.lessonId, studentId: lesson.studentId });
    } catch (error) {
      if (currentCommand()) {
        busy.current = false;
        if (!accessFailure(error)) {
          update({ actionState: { status: 'error', key: participationKey(lesson), message: error.message || 'Nie udało się zapisać zmiany.' } });
          if (error.code === 409) await refresh();
        }
      }
      return false;
    }
    if (!currentCommand()) return false;
    update({ actionState: { status: 'saved', key: participationKey(lesson), message: 'Zmiana została zapisana.' } });
    await refresh();
    if (currentCommand()) busy.current = false;
    return currentCommand();
  };
  const visible = data.scope === scope && canAccess ? data : { snapshot: null, loadState: initialLoad, actionState: idleAction };
  return { ...visible, refresh, cancel: lesson => command(service.cancelLessonParticipation, lesson), undo: lesson => command(service.undoLessonCancellation, lesson) };
}
